import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { 
    Plus, ChevronLeft, ChevronRight, Calendar, Image, Video, X,
    Upload, Trash2, Edit, Check, Copy, Instagram, Facebook, 
    Youtube, Linkedin, Twitter, Globe, FileImage, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel configuration
const channels = [
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700' },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'bg-sky-500' },
    { id: 'tiktok', name: 'TikTok', icon: Globe, color: 'bg-black' }
];

const statusOptions = [
    { id: 'draft', name: 'Entwurf', color: 'bg-gray-500' },
    { id: 'in_review', name: 'In Prüfung', color: 'bg-yellow-500' },
    { id: 'approved', name: 'Freigegeben', color: 'bg-green-500' },
    { id: 'revision_needed', name: 'Überarbeitung', color: 'bg-orange-500' },
    { id: 'published', name: 'Veröffentlicht', color: 'bg-blue-500' }
];

const ContentPlanManager = ({ customer, onClose, embedded = false }) => {
    const { api } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showPostModal, setShowPostModal] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [uploading, setUploading] = useState(false);
    
    const [postForm, setPostForm] = useState({
        date: '',
        channel: 'instagram',
        caption: '',
        hashtags: '',
        notes: '',
        status: 'draft'
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const fetchPosts = useCallback(async () => {
        try {
            const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            const res = await api.get(`/customers/${customer.id}/content-plan?month=${month}`);
            setPosts(res.data);
        } catch (error) {
            toast.error('Fehler beim Laden des Content-Plans');
        } finally {
            setLoading(false);
        }
    }, [api, customer.id, currentDate]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    // Days in month calculation
    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
        for (let i = 0; i < startPadding; i++) {
            days.push({ date: null, posts: [] });
        }

        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dayPosts = posts.filter(p => p.date === dateStr);
            days.push({ 
                date: dateStr, 
                day: d, 
                posts: dayPosts,
                isToday: new Date().toISOString().split('T')[0] === dateStr
            });
        }

        return days;
    }, [currentDate, posts]);

    const monthName = currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

    const navigateMonth = (direction) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);
            return newDate;
        });
    };

    const openCreateModal = (date) => {
        setEditingPost(null);
        setPostForm({
            date: date || new Date().toISOString().split('T')[0],
            channel: 'instagram',
            caption: '',
            hashtags: '',
            notes: '',
            status: 'draft'
        });
        setSelectedFile(null);
        setPreviewUrl(null);
        setShowPostModal(true);
    };

    const openEditModal = (post) => {
        setEditingPost(post);
        setPostForm({
            date: post.date,
            channel: post.channel,
            caption: post.caption || '',
            hashtags: post.hashtags?.join(', ') || '',
            notes: post.notes || '',
            status: post.status
        });
        setSelectedFile(null);
        setPreviewUrl(post.media_url || null);
        setShowPostModal(true);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSavePost = async () => {
        if (!postForm.date || !postForm.channel) {
            toast.error('Datum und Kanal sind erforderlich');
            return;
        }

        setUploading(true);
        try {
            const hashtagsArray = postForm.hashtags
                .split(/[,\s#]+/)
                .filter(tag => tag.trim())
                .map(tag => tag.trim());

            const postData = {
                date: postForm.date,
                channel: postForm.channel,
                caption: postForm.caption,
                hashtags: hashtagsArray,
                notes: postForm.notes,
                status: postForm.status
            };

            let postId = editingPost?.id;

            if (editingPost) {
                await api.put(`/customers/${customer.id}/content-plan/${postId}`, postData);
            } else {
                const res = await api.post(`/customers/${customer.id}/content-plan`, postData);
                postId = res.data.id;
            }

            // Upload media if selected
            if (selectedFile && postId) {
                const formData = new FormData();
                formData.append('file', selectedFile);
                await api.post(`/customers/${customer.id}/content-plan/${postId}/media`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            toast.success(editingPost ? 'Post aktualisiert!' : 'Post erstellt!');
            setShowPostModal(false);
            fetchPosts();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePost = async (postId) => {
        if (!window.confirm('Post wirklich löschen?')) return;
        
        try {
            await api.delete(`/customers/${customer.id}/content-plan/${postId}`);
            toast.success('Post gelöscht');
            fetchPosts();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const getChannelInfo = (channelId) => {
        return channels.find(c => c.id === channelId) || { name: channelId, icon: Globe, color: 'bg-gray-500' };
    };

    const getStatusInfo = (statusId) => {
        return statusOptions.find(s => s.id === statusId) || { name: statusId, color: 'bg-gray-500' };
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold">Content-Plan</h2>
                        <p className="text-sm text-muted-foreground">{customer.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-medium px-4 capitalize">{monthName}</span>
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => openCreateModal()}>
                        <Plus className="w-4 h-4 mr-2" />
                        Neuer Post
                    </Button>
                </div>
            </div>

            {/* Calendar Grid */}
            <Card className="shadow-soft">
                <CardContent className="p-4">
                    {/* Weekday Headers */}
                    <div className="grid grid-cols-7 gap-2 mb-2">
                        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Days Grid */}
                    <div className="grid grid-cols-7 gap-2">
                        {daysInMonth.map((day, index) => (
                            <div 
                                key={index}
                                onClick={() => day.date && openCreateModal(day.date)}
                                className={`min-h-[120px] p-2 rounded-lg border cursor-pointer transition-colors ${
                                    day.date 
                                        ? day.isToday 
                                            ? 'border-primary bg-primary/5' 
                                            : 'border-border/50 hover:bg-muted/30 hover:border-primary/30'
                                        : 'border-transparent'
                                }`}
                            >
                                {day.date && (
                                    <>
                                        <div className={`text-sm font-medium mb-2 ${
                                            day.isToday ? 'text-primary' : 'text-muted-foreground'
                                        }`}>
                                            {day.day}
                                        </div>
                                        <div className="space-y-1">
                                            {day.posts.map(post => {
                                                const channel = getChannelInfo(post.channel);
                                                const status = getStatusInfo(post.status);
                                                const ChannelIcon = channel.icon;
                                                
                                                return (
                                                    <motion.div
                                                        key={post.id}
                                                        initial={{ opacity: 0, scale: 0.9 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditModal(post);
                                                        }}
                                                        className={`rounded-md p-1.5 ${channel.color} text-white text-xs flex items-center gap-1 group relative`}
                                                    >
                                                        <ChannelIcon className="w-3 h-3" />
                                                        <span className="truncate flex-1">{post.caption?.substring(0, 15) || 'Kein Text'}</span>
                                                        <div className={`w-2 h-2 rounded-full ${status.color}`} title={status.name} />
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeletePost(post.id);
                                                            }}
                                                            className="opacity-0 group-hover:opacity-100 transition-opacity absolute -right-1 -top-1 bg-red-500 rounded-full p-0.5"
                                                        >
                                                            <X className="w-2 h-2" />
                                                        </button>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Status Legend */}
            <div className="flex flex-wrap items-center gap-4 justify-center">
                {statusOptions.map(status => (
                    <div key={status.id} className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${status.color}`} />
                        <span className="text-sm text-muted-foreground">{status.name}</span>
                    </div>
                ))}
            </div>

            {/* Create/Edit Post Modal */}
            <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingPost ? 'Post bearbeiten' : 'Neuer Post'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Datum</Label>
                                <Input
                                    type="date"
                                    value={postForm.date}
                                    onChange={(e) => setPostForm({ ...postForm, date: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Kanal</Label>
                                <Select 
                                    value={postForm.channel} 
                                    onValueChange={(v) => setPostForm({ ...postForm, channel: v })}
                                >
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {channels.map(channel => (
                                            <SelectItem key={channel.id} value={channel.id}>
                                                <div className="flex items-center gap-2">
                                                    <channel.icon className="w-4 h-4" />
                                                    {channel.name}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label>Beschreibung / Caption</Label>
                            <Textarea
                                value={postForm.caption}
                                onChange={(e) => setPostForm({ ...postForm, caption: e.target.value })}
                                placeholder="Post-Text eingeben..."
                                rows={4}
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label>Hashtags (komma- oder leerzeichengetrennt)</Label>
                            <Input
                                value={postForm.hashtags}
                                onChange={(e) => setPostForm({ ...postForm, hashtags: e.target.value })}
                                placeholder="marketing, agency, social"
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label>Interne Notizen</Label>
                            <Textarea
                                value={postForm.notes}
                                onChange={(e) => setPostForm({ ...postForm, notes: e.target.value })}
                                placeholder="Notizen für das Team..."
                                rows={2}
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label>Status</Label>
                            <Select 
                                value={postForm.status} 
                                onValueChange={(v) => setPostForm({ ...postForm, status: v })}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {statusOptions.map(status => (
                                        <SelectItem key={status.id} value={status.id}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3 h-3 rounded-full ${status.color}`} />
                                                {status.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Media Upload */}
                        <div>
                            <Label>Bild / Video</Label>
                            <div className="mt-2">
                                {previewUrl ? (
                                    <div className="relative inline-block">
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            className="max-h-[200px] rounded-lg"
                                        />
                                        <button
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setPreviewUrl(null);
                                            }}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                                        <FileImage className="w-8 h-8 text-muted-foreground mb-2" />
                                        <span className="text-sm text-muted-foreground">Klicken zum Hochladen</span>
                                        <input
                                            type="file"
                                            accept="image/*,video/*"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Customer Feedback Display */}
                        {editingPost?.customer_feedback && (
                            <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
                                <h4 className="text-sm font-medium text-orange-700 mb-1">Kundenfeedback</h4>
                                <p className="text-sm text-orange-600">{editingPost.customer_feedback}</p>
                            </div>
                        )}

                        {/* Quick Action Buttons for Status Change */}
                        {editingPost && (
                            <div className="border-t pt-4">
                                <Label className="mb-3 block">Schnellaktionen</Label>
                                <div className="flex flex-wrap gap-2">
                                    {postForm.status === 'draft' && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setPostForm({ ...postForm, status: 'in_review' })}
                                            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            Zur Prüfung einreichen
                                        </Button>
                                    )}
                                    {postForm.status === 'in_review' && (
                                        <>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => setPostForm({ ...postForm, status: 'approved' })}
                                                className="border-green-500 text-green-600 hover:bg-green-50"
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                Freigeben
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => setPostForm({ ...postForm, status: 'revision_needed' })}
                                                className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                            >
                                                <Edit className="w-4 h-4 mr-1" />
                                                Überarbeitung anfordern
                                            </Button>
                                        </>
                                    )}
                                    {postForm.status === 'revision_needed' && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setPostForm({ ...postForm, status: 'in_review' })}
                                            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            Erneut einreichen
                                        </Button>
                                    )}
                                    {postForm.status === 'approved' && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setPostForm({ ...postForm, status: 'published' })}
                                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            Als veröffentlicht markieren
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPostModal(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSavePost} disabled={uploading}>
                            {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingPost ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ContentPlanManager;
